import React, { useEffect, useRef, useState } from 'react';
import ApexCharts from 'apexcharts';
import jsVectorMap from 'jsvectormap';
import 'jsvectormap/dist/maps/world.js';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import moment from 'moment';
import 'bootstrap-daterangepicker/daterangepicker.css';
import { destroyScrollbars, initializeScrollbars } from './../../composables/scrollBar.js';

const dateRangePresets = {
	Today: [moment(), moment()],
	Yesterday: [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
	'Last 7 Days': [moment().subtract(6, 'days'), moment()],
	'Last 30 Days': [moment().subtract(29, 'days'), moment()],
	'This Month': [moment().startOf('month'), moment().endOf('month')],
	'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
};

function DashboardDateRangeFilter() {
	const [dateRange, setDateRange] = useState(() => ({
		start: moment().subtract(7, 'days'),
		end: moment()
	}));
	const settings = {
		startDate: dateRange.start,
		endDate: dateRange.end,
		maxSpan: { days: 60 },
		showDropdowns: true,
		parentEl: 'body',
		ranges: dateRangePresets,
		opens: 'left',
		drops: 'down',
		buttonClasses: ['btn', 'btn-sm'],
		applyButtonClasses: 'btn-primary',
		cancelButtonClasses: 'btn-default',
		locale: {
			applyLabel: 'Apply',
			cancelLabel: 'Cancel',
			customRangeLabel: 'Custom',
			daysOfWeek: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
			monthNames: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
			firstDay: 1
		}
	};

	return (
		<DateRangePicker initialSettings={settings} onCallback={(start, end) => setDateRange({ start, end })}>
			<a href="#/" className="btn px-3 btn-white d-flex align-items-center gap-2 text-truncate" id="daterange-filter">
				<iconify-icon icon="solar:calendar-linear" width="20" className="my-n1 ms-n1"></iconify-icon>
				<span>{dateRange.start.format('D MMMM YYYY')} - {dateRange.end.format('D MMMM YYYY')}</span>
				<b className="caret ms-1 opacity-5"></b>
			</a>
		</DateRangePicker>
	);
}

function cssVariable(name, fallback) {
	const value = getComputedStyle(document.body).getPropertyValue(name).trim();
	return value || fallback;
}

function chartColors() {
	return {
		primary: cssVariable('--bs-primary', '#348fe2'),
		dark: cssVariable('--bs-dark', '#212529'),
		blue: cssVariable('--bs-blue', '#348fe2'),
		teal: cssVariable('--bs-teal', '#00acac'),
		indigo: cssVariable('--bs-indigo', '#8753de'),
		orange: cssVariable('--bs-orange', '#f59c1a'),
		red: cssVariable('--bs-red', '#ff5b57'),
		lime: cssVariable('--bs-lime', '#90ca4b'),
		cyan: cssVariable('--bs-cyan', '#49b6d6'),
		green: cssVariable('--bs-green', '#198754'),
		gray: cssVariable('--bs-gray', '#6c757d'),
		gray200: cssVariable('--bs-gray-200', '#e9ecef'),
		gray400: cssVariable('--bs-gray-400', '#ced4da'),
		gray500: cssVariable('--bs-gray-500', '#adb5bd'),
		gray600: cssVariable('--bs-gray-600', '#6c757d'),
		body: cssVariable('--bs-body-color', '#6c757d'),
		border: cssVariable('--bs-border-color', 'rgba(0, 0, 0, .1)'),
		componentBg: cssVariable('--bs-component-bg', '#fff'),
		componentColor: cssVariable('--bs-component-color', '#212529'),
		componentColorRgb: cssVariable('--bs-component-color-rgb', '33, 37, 41'),
		primaryRgb: cssVariable('--bs-primary-rgb', '52, 143, 226'),
		white: cssVariable('--bs-white', '#fff')
	};
}

function renderChart(root, id, charts) {
	const container = root.querySelector('#' + id);
	const definition = charts[id];
	if (!container || !definition) return null;

	const baseOptions = definition.options || {
		chart: {
			type: definition.type,
			height: '100%',
			width: '100%',
			toolbar: { show: false },
			zoom: { enabled: false },
			animations: { enabled: true }
		},
		series: definition.series,
		colors: definition.colors,
		labels: definition.labels,
		dataLabels: { enabled: false },
		legend: definition.legend || { show: false },
		stroke: definition.stroke || { curve: 'smooth', width: 2 },
		fill: definition.fill || { opacity: 0.35 },
		grid: { borderColor: definition.gridColor || 'rgba(255,255,255,.15)' },
		xaxis: definition.xaxis || { labels: { style: { colors: '#8c96a0' } } },
		yaxis: definition.yaxis || { labels: { style: { colors: '#8c96a0' } } },
		plotOptions: definition.plotOptions,
		tooltip: { theme: 'dark' }
	};
	const chart = new ApexCharts(container, {
		...baseOptions,
		chart: {
			...(baseOptions.chart || {}),
			type: (baseOptions.chart && baseOptions.chart.type) || definition.type,
			height: (baseOptions.chart && baseOptions.chart.height) || '100%',
			width: (baseOptions.chart && baseOptions.chart.width) || '100%'
		},
		plotOptions: { line: {}, ...(baseOptions.plotOptions || {}) }
	});
	if (definition.type === 'heatmap') window.requestAnimationFrame(() => chart.render());
	else chart.render();
	return chart;
}

function renderMap(root, id, dark = false) {
	const map = root.querySelector('#' + id);
	if (!map) return null;

	if (map.__dashboardV4Map && map.__dashboardV4Map.destroy) {
		map.__dashboardV4Map.destroy();
	}
	map.innerHTML = '';

	const theme = cssVariable('--bs-app-theme', '#348fe2');
	const gray = cssVariable(dark ? '--bs-gray-700' : '--bs-gray-500', dark ? '#495057' : '#adb5bd');
	const background = dark ? cssVariable('--bs-gray-900', '#212529') : 'transparent';
	const mapInstance = new jsVectorMap({
		selector: '#' + id,
		map: 'world',
		zoomButtons: !dark,
		zoomOnScroll: false,
		hoverOpacity: 0.5,
		hoverColor: false,
		focusOn: { x: 0.5, y: 0.5, scale: 1 },
		markers: [
			{ name: 'Egypt', coords: [26.8206, 30.8025] },
			{ name: 'Russia', coords: [61.524, 105.3188] },
			{ name: 'Canada', coords: [56.1304, -106.3468] },
			{ name: 'Brazil', coords: [-14.235, -51.9253] }
		],
		markerStyle: { initial: { fill: theme, stroke: 'none', r: 5 }, hover: { fill: theme } },
		regionStyle: { initial: { fill: gray, fillOpacity: 0.5, stroke: 'none' }, hover: { fillOpacity: 0.8 } },
		backgroundColor: background
	});
	const destroy = () => {
		mapInstance.destroy();
		if (map.__dashboardV4Map && map.__dashboardV4Map.instance === mapInstance) {
			delete map.__dashboardV4Map;
			map.innerHTML = '';
		}
	};
	map.__dashboardV4Map = { destroy, instance: mapInstance };
	return { destroy };
}

function animateReferenceNumbers(root) {
	root.querySelectorAll('[data-animation="number"][data-value]').forEach((element) => {
		const value = Number(element.getAttribute('data-value'));
		if (!Number.isNaN(value)) {
			element.textContent = value.toLocaleString(undefined, {
				minimumFractionDigits: value % 1 ? 2 : 0,
				maximumFractionDigits: 2
			});
		}
	});
}

function animateReferenceProgress(root) {
	root.querySelectorAll('[data-animation="width"][data-value]').forEach((element) => {
		const value = element.getAttribute('data-value');
		const numericValue = value.replace('%', '');
		element.setAttribute('role', 'progressbar');
		element.setAttribute('aria-valuemin', '0');
		element.setAttribute('aria-valuemax', '100');
		element.setAttribute('aria-valuenow', numericValue);
		element.style.width = '0%';
		const applyWidth = () => {
			element.style.width = value;
		};
		window.requestAnimationFrame(applyWidth);
		window.setTimeout(applyWidth, 50);
	});
}

function getCharts(colors, root) {
	const rgba = (rgb, opacity) => 'rgba(' + rgb + ', ' + opacity + ')';
	const charts = {};

	const elementColors = (id) => {
		const element = root.querySelector('#' + id);
		const get = (name, fallback) =>
			(element && getComputedStyle(element).getPropertyValue(name).trim()) || fallback;
		const transparent = element && element.getAttribute('data-mode') === 'transparent';
		return {
			color: get('--bs-component-color', colors.componentColor),
			grid: get('--bs-component-color-rgb', colors.componentColorRgb),
			border: get('--bs-component-border-color', colors.border),
			accent: transparent ? get('--bs-component-color', colors.componentColor) : colors.primary,
			muted: get('--bs-component-color-rgb', colors.componentColorRgb),
			transparent
		};
	};
	const v4 = elementColors('revenueChart');
	charts.revenueChart = {
		type: 'area',
		options: {
			chart: {
				type: 'area',
				height: '100%',
				toolbar: { show: false },
				zoom: { enabled: false },
				animations: { easing: 'easeinout', speed: 600 }
			},
			series: [
				{
					name: 'Revenue',
					data: [15, 40, 58, 142, 55, 48, 60, 98, 115, 88, 102, 35, 70, 95, 78, 110, 130, 118, 92]
				},
				{ name: 'Profit', data: [20, 22, 38, 15, 22, 28, 35, 48, 62, 40, 50, 15, 12, 28, 18, 40, 58, 45, 60] }
			],
			stroke: { width: 2 },
			colors: [v4.accent, v4.transparent ? rgba(v4.grid, 0.5) : colors.gray400],
			dataLabels: { enabled: false },
			fill: { type: 'solid', opacity: 0.25 },
			grid: {
				borderColor: rgba(v4.grid, 0.1),
				strokeDashArray: 4,
				padding: { top: 0, right: 10, bottom: 0, left: 10 }
			},
			legend: { show: false },
			xaxis: {
				categories: [
					'Sun',
					'Mon',
					'Tue',
					'Wed',
					'Thu',
					'Fri',
					'Sat',
					'Sun',
					'Mon',
					'Tue',
					'Wed',
					'Thu',
					'Fri',
					'Sat',
					'Sun',
					'Mon',
					'Tue',
					'Wed',
					'Thu',
					'Fri',
					'Sat',
					'Sun',
					'Mon',
					'Tue',
					'Wed'
				],
				tickAmount: 5,
				axisBorder: { show: false },
				axisTicks: { show: false },
				labels: { style: { colors: v4.color, fontSize: '12px' } }
			},
			yaxis: { tickAmount: 4, labels: { style: { colors: v4.color } } },
			tooltip: { shared: true, intersect: false },
			markers: { size: 0 }
		}
	};
	const breakdown = elementColors('revenueBreakdownChart');
	charts.revenueBreakdownChart = {
		type: 'donut',
		options: {
			chart: { type: 'donut', height: 140 },
			labels: ['Subscriptions', 'One-time', 'Services'],
			series: [28430, 49020, 19490],
			colors: [breakdown.accent, breakdown.color, rgba(breakdown.muted, 0.5)],
			stroke: { width: 0 },
			plotOptions: {
				pie: {
					donut: {
						size: '70%',
						labels: {
							show: true,
							name: {
								show: true,
								fontSize: '12px',
								fontWeight: 600,
								color: rgba(breakdown.muted, 0.5),
								offsetY: -5
							},
							value: {
								show: true,
								fontSize: '16px',
								fontWeight: 700,
								color: breakdown.color,
								offsetY: 5,
								formatter: (value) => '$' + Number(value).toLocaleString()
							},
							total: {
								show: true,
								label: 'Total Revenue',
								fontSize: '12px',
								fontWeight: 700,
								color: rgba(breakdown.muted, 0.5),
								formatter: () => '$96,940'
							}
						}
					}
				}
			},
			dataLabels: { enabled: false },
			legend: { show: false }
		}
	};
	const campaign = elementColors('campaignBarChart');
	charts.campaignBarChart = {
		type: 'bar',
		options: {
			chart: { type: 'bar', height: '100%', toolbar: { show: false } },
			series: [{ name: 'Spend', data: [3200, 4100, 3800, 4500, 5200, 6100, 5500, 8250, 6700, 4600, 7320, 6100] }],
			labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
			colors: [campaign.accent],
			plotOptions: { bar: { borderRadius: 6, columnWidth: '45%' } },
			dataLabels: { enabled: false },
			xaxis: {
				labels: { style: { colors: campaign.color, fontSize: '12px' } },
				axisBorder: { show: false },
				axisTicks: { show: false }
			},
			yaxis: { labels: { style: { colors: campaign.color }, formatter: (value) => '$' + value / 1000 + 'k' } },
			grid: { borderColor: campaign.border, strokeDashArray: 4 }
		}
	};
	const trend = elementColors('customer-trend-chart');
	charts['customer-trend-chart'] = {
		type: 'line',
		options: {
			chart: {
				type: 'line',
				height: '100%',
				toolbar: { show: false },
				sparkline: { enabled: false },
				zoom: { enabled: false },
				pan: { enabled: false }
			},
			grid: {
				borderColor: rgba(trend.grid, 0.1),
				strokeDashArray: 4,
				padding: { top: 0, right: 10, bottom: 0, left: 10 }
			},
			stroke: { width: 2 },
			series: [{ name: 'Customers', data: [8200, 8300, 8450, 8600, 8800, 8950, 9103] }],
			xaxis: { labels: { style: { colors: trend.color, fontSize: '12px' } } },
			yaxis: { labels: { style: { colors: trend.color } } },
			colors: [trend.accent],
			tooltip: { enabled: true, y: { formatter: (value) => Number(value).toLocaleString() + ' users' } }
		}
	};
	const heatmapData = (day, max) => ({
		name: day,
		data: Array.from({ length: 12 }, (_, index) => ({ x: index * 2 + ':00', y: Math.floor(Math.random() * max) }))
	});
	charts.supportHeatmap = {
		type: 'heatmap',
		useDefaultChartDimensions: false,
		options: {
			chart: { type: 'heatmap', height: 150, toolbar: { show: false } },
			series: [
				heatmapData('Mon', 10),
				heatmapData('Tue', 8),
				heatmapData('Wed', 12),
				heatmapData('Thu', 6),
				heatmapData('Fri', 5),
				heatmapData('Sat', 4),
				heatmapData('Sun', 3)
			],
			legend: { show: false },
			dataLabels: { enabled: false },
			colors: [colors.green],
			plotOptions: {
				heatmap: {
					shadeIntensity: 0.5,
					colorScale: {
						ranges: [
							{ from: 0, to: 2, color: colors.gray200, name: 'Low' },
							{ from: 3, to: 5, color: colors.gray400, name: 'Medium' },
							{ from: 6, to: 9, color: colors.gray600, name: 'High' },
							{ from: 10, to: 20, color: colors.green, name: 'Critical' }
						]
					}
				}
			},
			xaxis: { labels: { show: false, style: { colors: colors.gray, fontSize: '10px' } } },
			yaxis: { labels: { show: false, style: { colors: colors.gray } } },
			grid: { borderColor: colors.border, padding: { top: 0, right: 0, bottom: 0, left: 0 } },
			tooltip: { y: { formatter: (value) => value + ' tickets' } }
		}
	};

	return charts;
}

function wireActions(root) {
	root.querySelectorAll('a[href="#/"]').forEach((link) => {
		link.addEventListener('click', (event) => event.preventDefault());
	});

	const exportButton = root.querySelector('#dashboard-export');
	if (exportButton) {
		exportButton.addEventListener('click', (event) => {
			event.preventDefault();
			const csv = 'Metric,Value\nTotal Sales,128940.55\nOrders,3824\nConversion,5.12%\n';
			const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
			const link = document.createElement('a');
			link.href = url;
			link.download = 'dashboard-export.csv';
			link.click();
			URL.revokeObjectURL(url);
		});
	}

}

function DashboardV4() {
	const rootRef = useRef(null);
	const [themeVersion, setThemeVersion] = useState(0);

	useEffect(() => {
		if (!rootRef.current) return undefined;
		const root = rootRef.current;
		const colors = chartColors();
		const charts = [];
		const maps = [renderMap(root, 'worldMap')];
		const definitions = getCharts(colors, root);

		animateReferenceNumbers(root);
		animateReferenceProgress(root);
		Object.keys(definitions).forEach((id) => {
			const chart = renderChart(root, id, definitions);
			if (chart) charts.push(chart);
		});
		const scrollbars = initializeScrollbars(root);
		wireActions(root);

		const refresh = () => setThemeVersion((current) => current + 1);
		document.addEventListener('theme-reload', refresh);
		return () => {
			document.removeEventListener('theme-reload', refresh);
			charts.forEach((chart) => chart.destroy());
			maps.filter(Boolean).forEach((map) => map.destroy && map.destroy());
		destroyScrollbars(scrollbars);
		};
	}, [themeVersion]);

	return (
		<div ref={rootRef}>
			<div className="d-flex mb-3">
				<div className="flex-1">
					<div className="d-xl-flex">
						<div className="flex-1 mb-2 mb-xl-0">
							<h1 className="page-header mb-0">Dashboard v4</h1>
							<div className="text-muted small2 fw-500">Your key metrics and insights at a glance.</div>
						</div>
							<div className="d-flex align-items-stretch align-items-sm-center gap-2 gap-sm-3 mt-3 mt-xl-0">
								<div className="d-sm-flex align-items-center btn-group">
									<DashboardDateRangeFilter />
								</div>
								<a
								href="#/"
								className="btn px-3 btn-white d-none d-sm-flex align-items-center gap-2 text-truncate"
								id="dashboard-export"
							>
								<iconify-icon
									icon="solar:export-linear"
									width="20"
									className="my-n1 ms-n1"
								></iconify-icon>
								Export
							</a>
						</div>
					</div>
				</div>
			</div>
			<div className="row g-3 mb-3">
				<div className="col-xl-7">
					<div className="card rounded-3 overflow-hidden border-0 h-100 bg-teal-400" data-bs-theme="dark">
						<div className="card-body position-relative z-1 text-white d-flex flex-column">
							<div className="d-flex mb-3">
								<div className="flex-fill">
									<div className="fw-bold fs-6">Overview · Revenue</div>
									<div className="fw-semibold text-white text-opacity-75 small">
										Month to date • consolidated view
									</div>
								</div>
								<div className="d-flex align-items-start gap-2">
									<a
										href="#/"
										className="btn btn-white btn-sm rounded-pill px-3 border-transparent bg-white bg-opacity-100 text-black"
									>
										Last 30d
									</a>
									<a
										href="#/"
										className="btn btn-white btn-sm rounded-pill px-3 text-white bg-white bg-opacity-15 border-transparent d-sm-block d-none"
									>
										Export
									</a>
								</div>
							</div>
							<div className="d-flex flex-1">
								<div className="flex-fill">
									<div className="fw-bolder small mb-1">TOTAL SALES</div>
									<div className="fs-1 fw-bolder mb-1 lh-sm">$128,940.55</div>
									<div className="fw-semibold mb-3">
										<span className="text-white">
											<b className="text-white">▲</b> <b>33.2%</b> vs last week
										</span>
									</div>
									<div className="w-md-350px">
										<hr className="opacity-3 text-white mt-md-3 mt-4" />
										<div className="row">
											<div className="col-6">
												<div className="card bg-none border-0">
													<div className="card-body d-flex gap-1 p-0 text-white">
														<div className="w-40px h-40px text-white d-flex align-items-center justify-content-center">
															<iconify-icon
																icon="solar:wallet-money-linear"
																className="fs-2 opacity-5"
															></iconify-icon>
														</div>
														<div className="flex-fill">
															<div className="fw-semibold small">Orders</div>
															<div className="fw-bolder">
																3,824{' '}
																<span className="fs-10px opacity-8 ms-2">▲ 59.2%</span>
															</div>
														</div>
													</div>
												</div>
											</div>
											<div className="col-6">
												<div className="card bg-none border-0">
													<div className="card-body d-flex gap-1 p-0 text-white">
														<div className="w-40px h-40px text-white d-flex align-items-center justify-content-center">
															<iconify-icon
																icon="solar:chart-square-linear"
																className="fs-2 opacity-5"
															></iconify-icon>
														</div>
														<div className="flex-fill">
															<div className="fw-semibold small">Conversion</div>
															<div className="fw-bolder">
																5.12%{' '}
																<span className="fs-10px opacity-8 ms-2">▲ 59.2%</span>
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
							<div className="row g-3 d-md-flex d-none mt-3">
								<div className="col-lg-4 col-md-6">
									<div className="card border-0 bg-white bg-opacity-15 rounded-3 bg-blur-3">
										<div className="card-body">
											<div className="fw-bold text-white mb-2 text-ellipsis">Top Product</div>
											<div className="fw-semibold text-white text-opacity-75 small mb-0 text-ellipsis">
												Wireless Headphones
											</div>
											<div className="fw-bold text-white text-ellipsis">
												1,420 units · $58,600
											</div>
											<div className="progress h-5px d-none">
												<div className="progress-bar bg-primary" style={{ width: '60%' }}></div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-lg-4 col-md-6">
									<div className="card border-0 bg-white bg-opacity-15 rounded-3 bg-blur-3">
										<div className="card-body">
											<div className="fw-bold text-white mb-2">Channel Mix</div>
											<div className="row">
												<div className="col-6">
													<div className="fw-semibold text-white text-opacity-75 small text-ellipsis">
														Subscriptions
													</div>
													<div className="fw-bold text-white text-ellipsis">$28,430</div>
												</div>
												<div className="col-6">
													<div className="fw-semibold text-white text-opacity-75 small text-ellipsis">
														One-time
													</div>
													<div className="fw-bold text-white text-ellipsis">$49,020</div>
												</div>
											</div>
										</div>
									</div>
								</div>
								<div className="col-lg-4 col-md-6">
									<div className="card border-0 bg-white bg-opacity-15 rounded-3 bg-blur-3">
										<div className="card-body">
											<div className="fw-bold text-white mb-2">System Health</div>
											<div className="d-flex align-items-center">
												<div className="flex-1">
													<div className="fw-semibold small text-white text-opacity-75">
														Uptime
													</div>
													<div className="fw-bold text-white">99.98%</div>
												</div>
												<div className="w-35px h-35px rounded-3 bg-white bg-opacity-15 d-flex align-items-center justify-content-center text-white">
													<iconify-icon
														icon="solar:server-square-cloud-bold-duotone"
														className="fs-3"
													></iconify-icon>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-end justify-content-end">
							<div className="position-absolute top-0 start-0 w-100 h-100 bg-gradient-45 bg-gradient-from-blue opacity-10 bg-gradient-to-transparent"></div>
							<img className="w-100 h-100 object-fit-cover" src="/color-admin/admin/react/assets/img/dashboard/img-29.svg"  alt="" />
						</div>
					</div>
				</div>
				<div className="col-xl-5 d-flex flex-column gap-3">
					<div className="row g-3">
						<div className="col-lg-6">
							<div
								className="card rounded-3 border-0 bg-gradient-135 bg-gradient-from-orange bg-gradient-to-red overflow-hidden"
								data-bs-theme="dark"
							>
								<div className="card-body text-white">
									<div className="d-flex justify-content-between align-items-start mb-1">
										<div className="flex-fill">
											<div className="fw-bold mb-1">Conversion Rate</div>
											<div className="fw-bold">
												<span className="fs-2">4.85%</span>
												<span className="fs-6">
													<span className="small ms-1 fw-semibold">(+0.92%)</span>
												</span>
											</div>
										</div>
										<div className="w-100px h-100px d-flex align-items-center justify-content-center position-absolute bottom-0 end-0 lh-1">
											<iconify-icon
												icon="solar:bag-check-bold-duotone"
												className="text-black text-opacity-50 me-n5 mb-n5"
												style={{ fontSize: '100px' }}
											></iconify-icon>
										</div>
									</div>
									<div className="small fw-semibold">
										Sessions: <strong className="fw-bold">152,400</strong>
									</div>
								</div>
							</div>
						</div>
						<div className="col-lg-6">
							<div
								className="card rounded-3 border-0 bg-gradient-135 bg-gradient-from-indigo bg-gradient-to-purple overflow-hidden"
								data-bs-theme="dark"
							>
								<div className="card-body text-white">
									<div className="d-flex justify-content-between align-items-start mb-1">
										<div className="flex-fill">
											<div className="fw-bold mb-1">New Customers</div>
											<div className="fw-bold">
												<span className="fs-2">842</span>
												<span className="fs-6 ms-1 fw-semibold">
													<span className="small">(+12.5%)</span>
												</span>
											</div>
										</div>
										<div className="w-100px h-100px d-flex align-items-center justify-content-center position-absolute bottom-0 end-0 lh-1">
											<iconify-icon
												icon="solar:users-group-rounded-bold-duotone"
												className="text-black text-opacity-50 me-n5 mb-n5"
												style={{ fontSize: '100px' }}
											></iconify-icon>
										</div>
									</div>
									<div className="small fw-semibold">
										New signups: <strong className="fw-bold">1,204</strong>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div className="card rounded-3 border-0 overflow-hidden bg-gray-900" data-bs-theme="dark">
						<div className="card-body text-body">
							<div className="d-flex align-items-center">
								<div className="flex-fill">
									<div className="fw-bold fs-6">Marketing Campaigns</div>
									<div className="fw-semibold text-body text-opacity-50 small">
										CTR, Spend &amp; Conversions
									</div>
								</div>
								<div className="d-none">
									<div className="fw-bold fs-4 text-white">
										442.8k <span className="fs-12px text-green">+13.2%</span>
									</div>
									<div className="d-none">Clicks</div>
									<a
										href="#/"
										className="d-none btn btn-default btn-sm rounded-pill px-3 text-white bg-white bg-opacity-15 border-transparent"
									>
										This Month
									</a>
								</div>
								<ul className="nav nav-pills small gap-2">
									<li className="nav-item">
										<a className="nav-link bg-primary text-white fw-bold px-2 py-1" href="#/">
											1D
										</a>
									</li>
									<li className="nav-item">
										<a className="nav-link fw-bold px-2 py-1" href="#/">
											7D
										</a>
									</li>
									<li className="nav-item">
										<a className="nav-link fw-bold px-2 py-1" href="#/">
											1M
										</a>
									</li>
									<li className="nav-item">
										<a className="nav-link fw-bold px-2 py-1" href="#/">
											1Y
										</a>
									</li>
								</ul>
							</div>
							<div className="mx-n3 mt-n1 my-n1 pe-1" style={{ height: '195px' }}>
								<div id="revenueChart" data-mode="default"></div>
							</div>
							<div className="row small gy-3 gx-4 mt-n1">
								<div className="col-4 d-flex align-items-center gap-2">
									<div>
										<div className="fw-bold text-white text-opacity-75 fs-12px">Email CTR</div>
										<div className="fw-bold text-body fs-6">
											13.2%{' '}
											<span className="fs-11px text-primary ms-1">
												<i className="fa fa-caret-up text-success d-none"></i>+1.8%
											</span>
										</div>
									</div>
								</div>
								<div className="col-4 d-flex align-items-center gap-2">
									<div>
										<div className="fw-bold text-white text-opacity-75 fs-12px">Ad Spend</div>
										<div className="fw-bold text-body fs-6">
											$8,750{' '}
											<span className="fs-11px text-white text-opacity-50 ms-1">
												<i className="fa fa-caret-down text-danger d-none"></i>-4.2%
											</span>
										</div>
									</div>
								</div>
								<div className="col-4">
									<div className="fw-bold text-white text-opacity-75 fs-12px">Conversions</div>
									<div className="fw-bold fs-6">
										2,184{' '}
										<span className="fs-11px text-primary ms-1">
											<i className="fa fa-caret-up text-success d-none"></i>+6.5%
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className="row g-3 mb-3">
				<div className="col-xl-5">
					<div className="card rounded-3 h-100 border-0">
						<div className="card-body">
							<div className="d-flex mb-3">
								<div className="flex-fill">
									<div className="fw-bold fs-6">Top Products</div>
									<div className="fw-semibold text-secondary small">Units &amp; revenue</div>
								</div>
								<div>
									<a href="#/" className="btn btn-default btn-sm rounded-pill px-3 border-transparent">
										Export
									</a>
								</div>
							</div>
							<div className="table-responsive mx-n3 mb-n3">
								<table className="table table-borderless mb-2 table-card align-middle fw-semibold text-nowrap small">
									<thead className="text-uppercase table-py-3px">
										<tr>
											<th className="">Product</th>
											<th className="">Units</th>
											<th className="">Revenue</th>
											<th className="text-end">Growth</th>
										</tr>
									</thead>
									<tbody className="table-py-9px">
										<tr>
											<td>
												<div className="d-flex align-items-center gap-3">
													<div className="w-50px h-50px d-flex align-items-center justify-content-center">
														<img
															src="/color-admin/admin/react/assets/img/product/iphone-17-pro-max.jpg"
															alt=""
															className="w-100 h-100 object-fit-cover rounded-3"
														/>
													</div>
													<div className="flex-fill">
														<div className="fw-bold">iPhone 17 Pro Max 1TB</div>
														<div className="small text-secondary fw-semibold">
															phone · best seller
														</div>
													</div>
												</div>
											</td>
											<td>1,420</td>
											<td>$58,600</td>
											<td className="text-end">+12%</td>
										</tr>
										<tr>
											<td>
												<div className="d-flex align-items-center gap-3">
													<div className="w-50px h-50px d-flex align-items-center justify-content-center">
														<img
															src="/color-admin/admin/react/assets/img/product/apple-watch-ultra-3.jpg"
															alt=""
															className="w-100 h-100 object-fit-cover rounded-3"
														/>
													</div>
													<div className="flex-fill">
														<div className="fw-bold">Smart Watch</div>
														<div className="small text-secondary fw-semibold">
															Wearables
														</div>
													</div>
												</div>
											</td>
											<td>1,120</td>
											<td>$45,900</td>
											<td className="text-end">+7%</td>
										</tr>
										<tr>
											<td>
												<div className="d-flex align-items-center gap-3">
													<div className="w-50px h-50px d-flex align-items-center justify-content-center">
														<img
															src="/color-admin/admin/react/assets/img/product/ipad-pro.jpg"
															alt=""
															className="w-100 h-100 object-fit-cover rounded-3"
														/>
													</div>
													<div className="flex-fill">
														<div className="fw-bold">iPad Pro 13-inch 1TB</div>
														<div className="small text-secondary fw-semibold">tablet</div>
													</div>
												</div>
											</td>
											<td>820</td>
											<td>$24,500</td>
											<td className="text-end">-3%</td>
										</tr>
									</tbody>
								</table>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-7">
					<div className="row">
						<div className="col-lg-6">
							<div className="card rounded-3 h-100 border-0">
								<div className="card-body">
									<div className="d-flex mb-3">
										<div className="flex-fill">
											<div className="fw-bold fs-6">Global Map</div>
											<div className="fw-semibold text-secondary small">Customer density</div>
										</div>
										<div>
											<a
												href="#/"
												className="btn btn-default btn-sm rounded-pill px-3 border-transparent"
											>
												Live
											</a>
										</div>
									</div>
									<div className="mx-n3 mb-3" style={{ height: '170px' }}>
										<div id="worldMap" className="h-100" data-mode="default"></div>
									</div>
									<div className="row text-center small">
										<div className="col">
											<div className="fw-bold">USA</div>
											<div className="text-primary fw-bold">39%</div>
										</div>
										<div className="col">
											<div className="fw-bold">EU</div>
											<div className="text-primary fw-bold">26%</div>
										</div>
										<div className="col">
											<div className="fw-bold">APAC</div>
											<div className="text-primary fw-bold">35%</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="col-lg-6">
							<div className="card rounded-3 h-100 border-0">
								<div className="card-body">
									<div className="d-flex mb-3">
										<div className="flex-fill">
											<div className="fw-bold fs-6">Revenue Breakdown</div>
											<div className="fw-semibold text-secondary small">Income category</div>
										</div>
										<div>
											<a
												href="#/"
												className="btn btn-default btn-sm rounded-pill px-3 border-transparent"
											>
												Last 30d
											</a>
										</div>
									</div>
									<div className="mb-3 bg-light2 rounded" style={{ height: '143px' }}>
										<div id="revenueBreakdownChart" className="h-100" data-mode="default"></div>
									</div>
									<div className="d-flex flex-column gap-1 small fw-semibold">
										<div className="d-flex">
											<div className="d-flex align-items-center gap-2 flex-fill">
												<div className="w-8px h-8px rounded bg-primary"></div>
												Subscriptions
											</div>
											<div className="fw-bold">$28,430</div>
										</div>
										<div className="d-flex">
											<div className="d-flex align-items-center gap-2 flex-fill">
												<div className="w-8px h-8px rounded bg-gray-900"></div>
												One-time
											</div>
											<div className="fw-bold">$49,020</div>
										</div>
										<div className="d-flex">
											<div className="d-flex align-items-center gap-2 flex-fill">
												<div className="w-8px h-8px rounded bg-gray-500"></div>
												Services
											</div>
											<div className="fw-bold">$19,490</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-5">
					<div className="card rounded-3 h-100 border-0">
						<div className="card-body">
							<div className="d-flex mb-3">
								<div className="flex-fill">
									<div className="fw-bold fs-6">Campaign Performance</div>
									<div className="fw-semibold text-secondary small">Top channels and spend</div>
								</div>
								<div>
									<a href="#/" className="btn btn-default btn-sm rounded-pill px-3 border-transparent">
										This Quarter
									</a>
								</div>
							</div>
							<div className="mb-2 mx-n3 mt-n3" style={{ height: '182px' }}>
								<div id="campaignBarChart" data-mode="default"></div>
							</div>
							<div className="row small">
								<div className="col">
									<div className="fw-bold">Email</div>
									<div className="fw-bold text-primary fs-6">CTR 13.2%</div>
									<div className="small text-secondary fw-semibold">Impr 52.1k · Conv 1.8k</div>
								</div>
								<div className="col">
									<div className="fw-bold">Google</div>
									<div className="fw-bold text-primary fs-6">$5,210</div>
									<div className="small text-secondary fw-semibold">ROAS 3.1</div>
								</div>
								<div className="col">
									<div className="fw-bold">Facebook</div>
									<div className="fw-bold text-primary fs-6">$3,540</div>
									<div className="small text-secondary fw-semibold">Reach 23.4k</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="col-xl-7">
					<div className="row g-3">
						<div className="col-lg-6">
							<div className="card rounded-3 h-100 border-0">
								<div className="card-body">
									<div className="d-flex mb-3">
										<div className="flex-fill">
											<div className="fw-bold fs-6">Customer Stats</div>
											<div className="fw-semibold text-secondary small">Key metrics</div>
										</div>
										<div>
											<a
												href="#/"
												className="btn btn-default btn-sm rounded-pill px-3 border-transparent"
											>
												Refresh
											</a>
										</div>
									</div>
									<div className="h-150px mb-3">
										<div id="customer-trend-chart" data-mode="default"></div>
									</div>
									<div className="d-flex flex-column gap-1 small fw-semibold">
										<div className="d-flex">
											<div className="flex-fill d-flex gap-2 align-items-center">
												<div className="w-8px h-8px rounded bg-primary"></div> Active
											</div>
											<div className="fw-bold">9,103</div>
										</div>
										<div className="d-flex">
											<div className="flex-fill d-flex gap-2 align-items-center">
												<div className="w-8px h-8px rounded bg-green"></div> Churn
											</div>
											<div className="fw-bold text-danger">1.9%</div>
										</div>
										<div className="d-flex">
											<div className="flex-fill d-flex gap-2 align-items-center">
												<div className="w-8px h-8px rounded bg-gray-500"></div> NPS
											</div>
											<div className="fw-bold">71</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<div className="col-lg-6">
							<div className="card rounded-3 h-100 border-0">
								<div className="card-body">
									<div className="d-flex mb-3">
										<div className="flex-fill">
											<div className="fw-bold fs-6">System &amp; Support</div>
											<div className="fw-semibold text-secondary small">Uptime, tickets</div>
										</div>
										<div>
											<a
												href="#/"
												className="btn btn-default btn-sm rounded-pill px-3 border-transparent"
											>
												Status
											</a>
										</div>
									</div>
									<div className="ms-n3 ps-6px h-150px mb-3">
										<div id="supportHeatmap"></div>
									</div>
									<div className="d-flex flex-column gap-1 small fw-semibold">
										<div className="d-flex">
											<div className="flex-fill d-flex gap-2 align-items-center">
												<div className="w-8px h-8px rounded bg-warning"></div> Open Tickets
											</div>
											<div className="fw-bold">42</div>
										</div>
										<div className="d-flex">
											<div className="flex-fill d-flex gap-2 align-items-center">
												<div className="w-8px h-8px rounded bg-gray-500"></div> Avg. Response
											</div>
											<div className="fw-bold">1h 12m</div>
										</div>
										<div className="d-flex">
											<div className="flex-fill d-flex gap-2 align-items-center">
												<div className="w-8px h-8px rounded bg-green"></div> Uptime
											</div>
											<div className="fw-bold text-green">99.97%</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}

export default DashboardV4;
